import type {
  DropzoneUploaderProps,
  FileInfo,
  FileUploaderError,
  ReactDropzoneProps,
} from '@meterup/atto';
import { DropzoneUploader } from '@meterup/atto';
import { uniqueId } from 'lodash-es';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

import { uploadToPresignedUrl } from '../helpers/uploadToPresignedUrl';

export type UploadedFileDetails = {
  s3Key: string;
  fileName: string;
  objectURL: string | null;
};

export interface UnifiedFileUploaderProps {
  /**
   * Requests a presigned upload URL and storage key for the file.
   */
  createUploadUrl: (file: File) => Promise<{ key: string; presignedUrl: string }>;
  /**
   * The types of files that the user is allowed to upload.
   */
  allowedTypes?: DropzoneUploaderProps['allowedTypes'];
  /**
   * Manually sets the accepted file mime types/extensions.
   * If used in conjunction with `allowedTypes`, the types/extensions specified in this prop will take precedence.
   */
  accept?: ReactDropzoneProps['accept'];
  /** Called when a selection is rejected by the file type or count limits. */
  onDropRejected?: ReactDropzoneProps['onDropRejected'];
  /**
   * The maximum number of files that the user is allowed to upload.
   */
  maxFiles?: number;
  /**
   * The maximum size of a file that the user is allowed to upload. If unspecified, there is no limit.
   */
  maxFileSizeBytes?: number;
  /**
   * Called when a file is successfully uploaded.
   */
  onFileUploaded?: (details: UploadedFileDetails) => void;
  /**
   * Called when a file that has previously been uploaded is removed by the user.
   */
  onFileRemoved?: (details: UploadedFileDetails) => void;
  /**
   * Called when the uploader's in-progress upload state changes.
   */
  onUploadingChange?: (isUploading: boolean) => void;
}

async function initFileUploader(
  createUploadUrl: UnifiedFileUploaderProps['createUploadUrl'],
  file: File,
  { onProgress }: { onProgress: (percentage: number) => void },
) {
  const presignedUrlDetails = await createUploadUrl(file);

  const { uploadPromise, abort } = uploadToPresignedUrl(file, presignedUrlDetails.presignedUrl, {
    contentType: file.type,
    onProgress,
  });

  return {
    s3Key: presignedUrlDetails.key,
    abortUpload: abort,
    uploadPromise,
  };
}

type FileUploadInfo = {
  didNotifyUploaded: boolean;
  error: FileUploaderError | null;
  uploadedS3Key: string | null;
  uploadPercentage: number;
  abortUploadFn?: () => void;
};

export interface UnifiedFileUploaderHandle {
  clear: () => void;
  addFiles: (files: File[]) => void;
}

export const UnifiedFileUploader = forwardRef<UnifiedFileUploaderHandle, UnifiedFileUploaderProps>(
  (props, ref) => {
    const [files, setFiles] = useState<(FileInfo & FileUploadInfo)[]>([]);

    const { createUploadUrl, onFileUploaded, onUploadingChange, onDropRejected } = props;

    const startUpload = useCallback(
      async (file: FileInfo & FileUploadInfo) => {
        function updateFileUploadDetails(id: string, patch: Partial<FileUploadInfo>) {
          setFiles((prevFiles) =>
            prevFiles.map((prevFile) => {
              if (prevFile.id === id) {
                return {
                  ...prevFile,
                  ...patch,
                };
              }
              return prevFile;
            }),
          );
        }

        if (props.maxFileSizeBytes && file.fileObject.size > props.maxFileSizeBytes) {
          updateFileUploadDetails(file.id, {
            error: {
              message: 'File size exceeds the maximum allowed size.',
              isValidationError: true,
            },
          });
          return;
        }

        // reset
        updateFileUploadDetails(file.id, {
          uploadPercentage: 0,
          uploadedS3Key: null,
          error: null,
          abortUploadFn: undefined,
          didNotifyUploaded: false,
        });

        try {
          const uploader = await initFileUploader(createUploadUrl, file.fileObject, {
            onProgress: (percentage) =>
              updateFileUploadDetails(file.id, { uploadPercentage: percentage }),
          });

          updateFileUploadDetails(file.id, {
            abortUploadFn: uploader.abortUpload,
          });

          await uploader.uploadPromise;
          updateFileUploadDetails(file.id, {
            uploadedS3Key: uploader.s3Key,
            uploadPercentage: 100,
          });
        } catch (error) {
          updateFileUploadDetails(file.id, {
            error: error instanceof Error ? error : new Error('File upload failed.'),
            uploadPercentage: 0,
          });
        }
      },
      [createUploadUrl, props.maxFileSizeBytes],
    );

    const addFiles = useCallback(
      (newFiles: File[]) => {
        const filesWithUploadPercentage = newFiles.map((file) => ({
          fileObject: file,
          uploadPercentage: 0,
          id: uniqueId(),
          didNotifyUploaded: false,
          uploadedS3Key: null,
          error: null,
        }));

        setFiles((prevFiles) => [...prevFiles, ...filesWithUploadPercentage]);

        filesWithUploadPercentage.forEach(startUpload);
      },
      [startUpload],
    );

    // This is wrapped is useCallback as recommended by the docs for Dropzone
    const onDrop = useCallback<DropzoneUploaderProps['onDrop']>(
      (droppedFiles, rejections, event) => {
        if (rejections.length > 0) onDropRejected?.(rejections, event);
        addFiles(droppedFiles);
      },
      [addFiles, onDropRejected],
    );

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          setFiles((prevFiles) => {
            prevFiles.forEach((f) => f.abortUploadFn?.());
            return [];
          });
        },
        addFiles,
      }),
      [addFiles],
    );

    useEffect(() => {
      const isUploading = files.some((file) => !file.uploadedS3Key && !file.error);
      onUploadingChange?.(isUploading);
    }, [files, onUploadingChange]);

    useEffect(() => {
      files.forEach((file) => {
        if (!file.didNotifyUploaded && file.uploadPercentage === 100 && file.uploadedS3Key) {
          onFileUploaded?.({
            s3Key: file.uploadedS3Key,
            fileName: file.fileObject.name,
            objectURL: URL.createObjectURL(file.fileObject),
          });
          // marking didNotifyUploaded prevents the effect logic from firing multiple times
          // even _if_ the user of the component accidentally passes us an unstable onFileUploaded fn
          setFiles((prevFiles) =>
            prevFiles.map((prevFile) => {
              if (prevFile.id === file.id) {
                return { ...prevFile, didNotifyUploaded: true };
              }
              return prevFile;
            }),
          );
        }
      });
    }, [files, onFileUploaded]);

    return (
      <DropzoneUploader
        maxFiles={props.maxFiles}
        accept={props.accept}
        allowedTypes={props.allowedTypes}
        onDrop={onDrop}
        onFileRemoved={(id) => {
          setFiles((prevFiles) =>
            prevFiles.filter((file) => {
              if (file.id === id) {
                if (file.abortUploadFn) {
                  file.abortUploadFn();
                }
                if (props.onFileRemoved && file.didNotifyUploaded && file.uploadedS3Key) {
                  props.onFileRemoved({
                    s3Key: file.uploadedS3Key,
                    fileName: file.fileObject.name,
                    objectURL: null,
                  });
                }
                return false;
              }
              return true;
            }),
          );
        }}
        files={files}
        onRetryUpload={(id) => {
          const file = files.find((f) => f.id === id);
          if (!file) return;
          startUpload(file);
        }}
      />
    );
  },
);
